import { useInitialMonth, orderMonthOffset } from './usePinnedOrderMonth.js';
/**
 * «Моя зарплата» — сотрудник видит свои деньги и то, из чего они сложились.
 *
 * Экран НИЧЕГО не показывает, пока не введён пароль. В лаборатории за одним
 * компьютером работают по очереди и из программы обычно не выходят: без
 * пароля сосед увидел бы чужую зарплату, просто открыв раздел. Поэтому числа
 * приходят только в ответ на запрос с паролем, живут в памяти открытой
 * страницы и исчезают при уходе с неё или перезагрузке.
 *
 * Считает всё сервер и только для вошедшего: сюда приезжают готовые числа,
 * без списка сотрудников и без финансов лаборатории. Ни выручки, ни аренды,
 * ни материалов, ни базы, от которой считается доля, здесь нет и в ответе
 * сервера тоже: сотруднику важна своя доля, а не обороты лаборатории.
 */

import { useState } from 'react';
import { Eye, EyeOff, Lock, Wallet } from 'lucide-react';
import {
  formatKop,
  formatMonthKey,
  isFirstMonth,
  monthKeyForOffset,
  shareApplies,
  todayIso,
} from '@otc/shared';

import { fetchMySalary, type ApiMySalary } from '../api/queries.js';
import { SectionHeader, Spinner } from '../components/ui.js';
import { MonthSwitcher } from '../components/MonthSwitcher.js';
import { STATUS_COLORS } from '../theme.js';

export function MySalary() {
  const today = todayIso();
  const initialMonth = useInitialMonth(today);
  const [offset, setOffset] = useState(() => orderMonthOffset(today, initialMonth));
  const monthKey = monthKeyForOffset(today, offset);

  const [password, setPassword] = useState('');
  const [visible, setVisible] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** Числа живут ТОЛЬКО здесь: ни в кеше запросов, ни в хранилище браузера. */
  const [salary, setSalary] = useState<ApiMySalary | null>(null);

  /** Перейти на выбранный месяц, не спрашивая пароль заново. */
  const перейтиНаМесяц = (ключ: string) => {
    const [год, месяц] = ключ.split('-').map(Number) as [number, number];
    const [нынешнийГод, нынешнийМесяц] = monthKeyForOffset(today, 0)
      .split('-')
      .map(Number) as [number, number];
    const шаг = (год - нынешнийГод) * 12 + (месяц - нынешнийМесяц);
    if (шаг === offset) return;
    setOffset(шаг);
    void open(ключ, password);
  };

  const open = async (месяц: string, пароль: string) => {
    setBusy(true);
    setError(null);
    try {
      setSalary(await fetchMySalary(пароль, месяц));
    } catch (e) {
      setSalary(null);
      setError(e instanceof Error ? e.message : 'Не удалось показать зарплату');
    } finally {
      setBusy(false);
    }
  };

  /*
   * Листание месяцев пароль НЕ спрашивает заново: человек уже подтвердил, что
   * это он, и держать его за дверью на каждом шаге — значит заставить набирать
   * пароль шесть раз подряд ради полугодовой картины. Пароль остаётся в памяти
   * страницы и пропадает вместе с ней.
   */
  const шагМесяца = (шаг: number) => {
    const следующий = offset + шаг;
    setOffset(следующий);
    if (salary) void open(monthKeyForOffset(today, следующий), password);
  };

  return (
    <div>
      <SectionHeader
        icon={Wallet}
        title="Моя зарплата"
        subtitle={salary ? `За ${formatMonthKey(monthKey).toLowerCase()}` : 'Введите пароль, чтобы посмотреть'}
      />

      {!salary ? (
        <form
          className="max-w-sm rounded-xl border p-4"
          style={{ borderColor: 'var(--otc-line)', background: 'var(--otc-surface)' }}
          onSubmit={(e) => {
            e.preventDefault();
            void open(monthKey, password);
          }}
        >
          <p className="mb-3 flex items-start gap-2 text-xs text-ink2">
            <Lock size={14} className="mt-0.5 flex-shrink-0" />
            {/*
              Объясняем, ЗАЧЕМ пароль: без объяснения повторный ввод выглядит
              придиркой программы, которая и так знает, кто вошёл.
            */}
            Пароль спрашивается каждый раз — чтобы за вашим компьютером зарплату
            не посмотрел кто-то другой.
          </p>

          <label className="mb-2 block">
            <span className="mb-1 block text-xs text-ink2">Ваш пароль</span>
            <span className="flex items-center gap-2">
              <input
                autoFocus
                className="otc-input"
                type={visible ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Тот же, что при входе"
              />
              <button
                type="button"
                onClick={() => setVisible((v) => !v)}
                className="otc-btn otc-btn-ghost otc-btn-icon"
                title={visible ? 'Скрыть пароль' : 'Показать пароль'}
              >
                {visible ? <EyeOff size={15} /> : <Eye size={15} />}
              </button>
            </span>
          </label>

          {error && (
            <p className="mb-2 text-xs" style={{ color: STATUS_COLORS.danger }}>
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={busy || password.length === 0}
            className="otc-btn otc-btn-primary"
            style={{ background: 'var(--otc-accent-dark)', color: '#fff' }}
          >
            {busy ? 'Проверяем…' : 'Показать зарплату'}
          </button>
        </form>
      ) : busy ? (
        <Spinner label="Считаем…" />
      ) : (
        <ВидЗарплаты
          salary={salary}
          monthKey={monthKey}
          onPrev={() => шагМесяца(-1)}
          onNext={() => шагМесяца(1)}
          nextDisabled={offset >= 0}
          onМесяц={перейтиНаМесяц}
        />
      )}
    </div>
  );
}

/**
 * ВИД зарплаты — без пароля и без запросов: только показ переданных чисел.
 *
 * Вынесен отдельно, потому что этот же экран смотрит владелец из карточки
 * сотрудника («Сотрудники» → «Кабинет сотрудника»). Держать два похожих
 * разбора значило бы однажды поправить один и забыть другой — и владелец
 * увидел бы не то, что видит человек.
 */
export function ВидЗарплаты({
  salary,
  monthKey,
  onPrev,
  onNext,
  nextDisabled = false,
  onМесяц,
  цвет,
}: {
  salary: ApiMySalary;
  monthKey: string;
  onPrev: () => void;
  onNext: () => void;
  nextDisabled?: boolean;
  /** Щелчок по столбику диаграммы. */
  onМесяц: (monthKey: string) => void;
  /** Цвет сотрудника из «Сотрудников» — им красятся столбики. */
  цвет?: string;
}) {
  /*
   * ПРЕЖНИЙ ПОРЯДОК: до сентября 2026 зарплата не была долей от дохода.
   *
   * Платили сдельно и вносили сумму руками. Разбор со ставкой за такой месяц
   * описывал бы расчёт, которого не было, — человек увидел бы проценты и
   * решил, что ему тогда считали по ним.
   */
  const доля = shareApplies(monthKey);

  return (
    <div className="max-w-xl">
          {/*
            Переключатель месяца — ОБЩИЙ, тот же, что в «Заказах» и
            «Финансах»: белые кнопки и название в рамке. Здесь он был
            нарисован по-своему — прозрачными кнопками-стрелками, — и
            единственный во всей программе выбивался из ряда.
          */}
          <div className="mb-4">
            <MonthSwitcher
              label={formatMonthKey(monthKey)}
              onPrev={onPrev}
              onNext={onNext}
              nextDisabled={nextDisabled}
              /* Левее января 2026 в программе ничего нет. */
              prevDisabled={isFirstMonth(monthKey)}
            />
          </div>

          {/*
            Крупным числом — ОСТАТОК: это то, что человек получит на руки.
            Заработок месяца стоит строкой ниже, вместе со всем разбором.
          */}
          <div
            className="mb-3 flex items-baseline justify-between gap-3 rounded-xl border p-4"
            style={{ borderColor: 'var(--otc-line)', background: 'var(--otc-surface)' }}
          >
            <span className="min-w-0">
              <span className="block text-xs text-ink2">
                {доля ? 'Остаток к выплате' : 'Выплачено'}
              </span>
              <span className="text-[11px] text-ink2">
                {!доля
                  ? 'за этот месяц зарплата была записана вручную'
                  : salary.advanceKop > 0
                    ? `зарплата минус аванс ${formatKop(salary.advanceKop)} ₽`
                    : 'аванса не было — платим целиком'}
              </span>
            </span>
            <span className="tabular flex-shrink-0 text-xl font-semibold text-ink">
              {formatKop(salary.remainderKop)} ₽
            </span>
          </div>

          <div
            className="space-y-1.5 rounded-xl border p-4 text-sm"
            style={{ borderColor: 'var(--otc-line)', background: 'var(--otc-surface)' }}
          >
            {/*
              Доля названа СТАВКОЙ, а не долей «от базы»: саму базу сотрудник
              не видит — это выручка лаборатории за вычетом материалов и
              аренды. Упоминание базы без числа только рождало бы вопрос,
              от чего же считали.
            */}
            {доля && (
              <Строка
                подпись={`доля по ставке ${salary.percent}%`}
                значение={formatKop(salary.shareKop)}
                зачёркнуто={salary.atMinimum}
              />
            )}

            {salary.atMinimum && (
              <Строка
                подпись="минимальная зарплата"
                значение={formatKop(salary.minSalaryKop)}
                цвет={STATUS_COLORS.warning}
              />
            )}

            {/*
              Премия показывается, ТОЛЬКО когда она есть.

              Строка «премия 0 ₽» стояла в разборе всегда и читалась как
              напоминание о том, чего не дали. Ноль здесь не сведение: премии
              за месяц просто не было — как не было отпускных, и их строка
              тоже не рисуется.
            */}
            {salary.bonusKop > 0 && (
              <Строка подпись="премия" значение={`+${formatKop(salary.bonusKop)}`} />
            )}
            {salary.vacationKop > 0 && (
              <Строка подпись="отпускные" значение={`+${formatKop(salary.vacationKop)}`} />
            )}

            {salary.manual && доля && (
              <>
                <Строка
                  подпись="сумма назначена вручную"
                  значение={formatKop(salary.salaryKop)}
                  цвет={STATUS_COLORS.warning}
                />
                <p className="text-[11px] leading-snug text-ink2">
                  по расчёту вышло бы {formatKop(salary.computedKop)} ₽
                  {salary.manualNote ? ` · ${salary.manualNote}` : ''}
                </p>
              </>
            )}
            {!доля && salary.manualNote && (
              <p className="text-[11px] leading-snug text-ink2">{salary.manualNote}</p>
            )}

            <div className="mt-1 border-t pt-1.5" style={{ borderColor: 'var(--otc-line)' }}>
              <Строка подпись="итого за месяц" значение={formatKop(salary.salaryKop)} жирно />
              {salary.advanceKop > 0 && (
                <Строка
                  подпись="аванс выдан"
                  значение={`−${formatKop(salary.advanceKop)}`}
                  цвет={STATUS_COLORS.warning}
                />
              )}
            </div>
          </div>

          <Столбики
            история={salary.history}
            показан={monthKey}
            onВыбор={onМесяц}
            цвет={цвет}
          />

    </div>
  );
}
/**
 * Зарплата за год СТОЛБИКАМИ.
 *
 * Одно число в месяц ничего не говорит: «много» или «мало» видно только
 * рядом с соседними месяцами. Листать их стрелками — десяток переходов, и
 * каждый надо удержать в голове; столбики рядом читаются разом.
 *
 * Ряд — месяцы ЭТОГО ГОДА и он НЕ МЕНЯЕТСЯ при переключении: нажатие на
 * столбик открывает его месяц, а сам ряд остаётся на месте. Прежде ряд
 * отсчитывался от показанного месяца, и при нажатии столбики переезжали,
 * мельчали, а у начала учёта диаграмма пропадала совсем.
 *
 * Ряд ОДИН, поэтому и цвет один — цвет темы: показанный месяц полным
 * тоном, остальные бледным. Разными цветами столбики красить нельзя: цвет
 * тогда начинает означать что-то, чего нет.
 *
 * Подписи под столбиками — только месяц; сумма показывается при наведении
 * и полностью разобрана выше, в самой карточке. Число над каждым столбиком
 * превратило бы диаграмму в ту же таблицу, только кривую.
 */
function Столбики({
  история,
  показан,
  onВыбор,
  цвет,
}: {
  история: { monthKey: string; salaryKop: number }[];
  показан: string;
  onВыбор: (monthKey: string) => void;
  /**
   * Цвет сотрудника — тот же, что закреплён за ним в «Сотрудниках».
   *
   * Иначе в кабинете столбики цвета темы, а на общем графике выплат рядом —
   * свои, и два ряда об одних и тех же деньгах читаются как о разных.
   */
  цвет?: string;
}) {
  /* Один месяц — тоже диаграмма: в январе она не должна исчезать. */
  if (история.length === 0) return null;

  /** Высота поля диаграммы в точках: выше — и карточка начинает листаться. */
  const ПОЛЕ = 72;
  const предел = Math.max(...история.map((м) => м.salaryKop), 1);

  return (
    <div
      className="mt-3 rounded-xl border p-3"
      style={{ borderColor: 'var(--otc-line)', background: 'var(--otc-surface)' }}
    >
      <p className="mb-2 text-xs text-ink2">Этот год · нажмите на столбик, чтобы открыть месяц</p>

      {/*
        Высота столбика — В ТОЧКАХ, а не в процентах.

        Процент считается от высоты родителя, а она у кнопки задаётся
        содержимым: получалась ничья высота, и столбики вставали вразнобой,
        не от общей линии. В точках такой неопределённости нет.
      */}
      <div className="flex items-end gap-1.5">
        {история.map((м) => {
          const этот = м.monthKey === показан;
          /* Пустой месяц тоже занимает место: пропуск в ряду читался бы
             как «данных нет», а не как «в тот месяц не заработано». */
          const высота = Math.max(2, Math.round((м.salaryKop / предел) * ПОЛЕ));
          return (
            <button
              key={м.monthKey}
              type="button"
              onClick={() => onВыбор(м.monthKey)}
              title={`${formatMonthKey(м.monthKey)} — ${formatKop(м.salaryKop)} ₽`}
              aria-label={`${formatMonthKey(м.monthKey)}: ${formatKop(м.salaryKop)} рублей`}
              className="flex min-w-0 flex-1 flex-col justify-end gap-1"
            >
              <span
                className="block w-full rounded-t transition-[height]"
                style={{
                  height: высота,
                  /* Свой цвет сотрудника; показанный месяц — полным тоном. */
                  background: этот
                    ? (цвет ?? 'var(--otc-accent)')
                    : `color-mix(in srgb, ${цвет ?? 'var(--otc-accent)'} 30%, transparent)`,
                }}
              />
              <span
                className={`block truncate text-center text-[10px] ${этот ? 'font-medium text-ink' : 'text-ink2'}`}
              >
                {formatMonthKey(м.monthKey).replace(/ d{4}$/, '').slice(0, 3)}
              </span>
            </button>
          );
        })}      </div>
    </div>
  );
}

/** Строка разбора: подпись слева, число справа. */
function Строка({
  подпись,
  значение,
  цвет,
  зачёркнуто,
  жирно,
}: {
  подпись: string;
  значение: string;
  цвет?: string;
  зачёркнуто?: boolean;
  жирно?: boolean;
}) {
  return (
    <div className="flex justify-between gap-3">
      <span className={зачёркнуто ? 'text-ink2 line-through opacity-60' : 'text-ink2'}>{подпись}</span>
      <span
        className={`tabular flex-shrink-0 ${жирно ? 'font-semibold' : ''} ${
          зачёркнуто ? 'line-through opacity-60' : ''
        }`}
        style={{ color: цвет ?? 'var(--otc-ink)' }}
      >
        {значение} ₽
      </span>
    </div>
  );
}
